import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
  Sparkles,
  Globe2,
  GraduationCap,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { useI18n } from "@/i18n/I18nContext";
import { CAREERS } from "@/i18n/extras";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?fm=jpg&q=80&w=2000&auto=format&fit=crop";

const VALUE_ICONS = [Sparkles, Globe2, GraduationCap];

const HumanResourcesPage = () => {
  const { locale } = useI18n();
  const t = CAREERS[locale] || CAREERS.en;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
    position: "",
    department: "engineering",
    experience: "",
    current_role: "",
    linkedin: "",
    salary: "",
    availability: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => {
    const v = typeof e === "string" ? e : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) er.full_name = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = true;
    if (!form.phone.trim() || form.phone.trim().length < 6) er.phone = true;
    if (!form.position.trim() || form.position.trim().length < 2) er.position = true;
    if (!form.experience.trim()) er.experience = true;
    if (!form.message.trim() || form.message.trim().length < 10) er.message = true;
    if (!form.consent) er.consent = true;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim() || null,
        position: form.position.trim(),
        department: form.department,
        experience: form.experience.trim(),
        current_role: form.current_role.trim() || null,
        linkedin: form.linkedin.trim() || null,
        salary: form.salary.trim() || null,
        availability: form.availability.trim() || null,
        message: form.message.trim(),
        consent: form.consent,
        locale,
      };
      await axios.post(`${API}/hr-application`, payload);
      setSubmitted(true);
      toast.success(t.successTitle, { description: t.successBody });
      setForm({
        full_name: "",
        email: "",
        phone: "",
        city: "",
        position: "",
        department: "engineering",
        experience: "",
        current_role: "",
        linkedin: "",
        salary: "",
        availability: "",
        message: "",
        consent: false,
      });
    } catch (err) {
      console.error("HR submit error", err);
      toast.error(t.errorTitle, { description: t.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="hr-page">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden hero-wash">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-slate-500 hover:text-[hsl(var(--primary))]"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Home
              </Link>
              <div className="mt-3 text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
                {t.eyebrow}
              </div>
              <h1
                className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[hsl(var(--primary))]"
                data-testid="hr-hero-title"
              >
                {t.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-slate-700">
                {t.lead}
              </p>
              <div className="mt-7">
                <Button
                  asChild
                  size="lg"
                  className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white"
                >
                  <a href="#hr-form" data-testid="hr-hero-cta">
                    {t.submit}
                    <Send className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[hsl(var(--accent))]/10 blur-2xl" />
                <div className="overflow-hidden rounded-3xl border border-[rgba(201,162,39,0.3)] aspect-[4/5]">
                  <img src={HERO_IMG} alt="Careers" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Value props */}
        <section className="py-16 md:py-20 bg-[hsl(var(--tbs-cream-50))]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl md:text-4xl text-[hsl(var(--primary))]">
              {t.sectionTitle}
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {t.valueProps.map((v, i) => {
                const Icon = VALUE_ICONS[i] || Briefcase;
                return (
                  <Card
                    key={v.title}
                    data-testid={`hr-value-${i}`}
                    className="card-lift bg-white border border-slate-200 rounded-2xl p-7"
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 font-display text-xl text-[hsl(var(--primary))]">
                      {v.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.body}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Form */}
        <section id="hr-form" className="py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
                {t.eyebrow}
              </div>
              <h2 className="mt-3 font-display text-3xl md:text-4xl text-[hsl(var(--primary))]">
                {t.formTitle}
              </h2>
              <p className="mt-3 text-slate-700">{t.formLead}</p>
            </div>

            <Card className="mt-10 bg-white border border-slate-200 rounded-3xl p-6 md:p-8">
              <form onSubmit={onSubmit} data-testid="hr-application-form" className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t.labels.fullName} required error={errors.full_name} requiredLabel={t.required}>
                    <Input
                      value={form.full_name}
                      onChange={onChange("full_name")}
                      className="h-11"
                      data-testid="hr-full-name"
                      required
                    />
                  </Field>
                  <Field label={t.labels.email} required error={errors.email} requiredLabel={t.required}>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      className="h-11"
                      data-testid="hr-email"
                      required
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t.labels.phone} required error={errors.phone} requiredLabel={t.required}>
                    <Input
                      value={form.phone}
                      onChange={onChange("phone")}
                      className="h-11"
                      placeholder="+90 5xx xxx xx xx"
                      data-testid="hr-phone"
                      required
                    />
                  </Field>
                  <Field label={t.labels.city}>
                    <Input
                      value={form.city}
                      onChange={onChange("city")}
                      className="h-11"
                      placeholder="Istanbul, Ankara..."
                      data-testid="hr-city"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t.labels.position} required error={errors.position} requiredLabel={t.required}>
                    <Input
                      value={form.position}
                      onChange={onChange("position")}
                      placeholder={t.placeholders.position}
                      className="h-11"
                      data-testid="hr-position"
                      required
                    />
                  </Field>
                  <Field label={t.labels.department}>
                    <Select
                      value={form.department}
                      onValueChange={(v) => setForm((f) => ({ ...f, department: v }))}
                    >
                      <SelectTrigger data-testid="hr-department" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {t.departments.map((d) => (
                          <SelectItem key={d.value} value={d.value} data-testid={`hr-dept-${d.value}`}>
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t.labels.experience} required error={errors.experience} requiredLabel={t.required}>
                    <Input
                      value={form.experience}
                      onChange={onChange("experience")}
                      placeholder={t.placeholders.experience}
                      className="h-11"
                      data-testid="hr-experience"
                      required
                    />
                  </Field>
                  <Field label={t.labels.currentRole}>
                    <Input
                      value={form.current_role}
                      onChange={onChange("current_role")}
                      className="h-11"
                      data-testid="hr-current-role"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t.labels.linkedin}>
                    <Input
                      value={form.linkedin}
                      onChange={onChange("linkedin")}
                      placeholder="https://linkedin.com/in/..."
                      className="h-11"
                      data-testid="hr-linkedin"
                    />
                  </Field>
                  <Field label={t.labels.salary}>
                    <Input
                      value={form.salary}
                      onChange={onChange("salary")}
                      className="h-11"
                      data-testid="hr-salary"
                    />
                  </Field>
                </div>

                <Field label={t.labels.availability}>
                  <Input
                    value={form.availability}
                    onChange={onChange("availability")}
                    placeholder={t.placeholders.availability}
                    className="h-11"
                    data-testid="hr-availability"
                  />
                </Field>

                <Field label={t.labels.message} required error={errors.message} requiredLabel={t.required}>
                  <Textarea
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder={t.placeholders.message}
                    rows={5}
                    data-testid="hr-message"
                    required
                  />
                </Field>

                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="hr-consent"
                    checked={form.consent}
                    onCheckedChange={(v) => {
                      setForm((f) => ({ ...f, consent: !!v }));
                      setErrors((er) => ({ ...er, consent: undefined }));
                    }}
                    data-testid="hr-consent"
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="hr-consent"
                    className={`text-xs leading-relaxed ${errors.consent ? "text-red-600" : "text-slate-600"}`}
                  >
                    {t.labels.consent}
                  </Label>
                </div>

                <div className="flex items-center justify-between gap-4 pt-2">
                  {submitted ? (
                    <div
                      data-testid="hr-success"
                      className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {t.successTitle}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">{t.confidential}</span>
                  )}
                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    data-testid="hr-submit"
                    className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t.submitting}
                      </>
                    ) : (
                      <>
                        {t.submit}
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

const Field = ({ label, required, error, requiredLabel, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
      {label} {required && <span className="text-[hsl(var(--accent))]">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {error && <div className="mt-1 text-xs text-red-600">{requiredLabel || "Required"}</div>}
  </div>
);

export default HumanResourcesPage;
